import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Zap, Mail, CheckCircle2, AlertTriangle, Send, Loader2, Bell, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, goldBtn } from "@/pages/_shared";

const ACTION_ICON = { notify: Bell, task: ListChecks, email: Mail, audit: CheckCircle2 };

export default function Automations() {
  const { can, isGroupView, activeCompany, activeCompanyId } = useOrg();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/automations");
    setData(data);
    if (can("settings.manage")) {
      try { const h = await api.get("/email/health"); setHealth(h.data); } catch (e) {}
    }
  }, [can]);
  useEffect(() => { load(); }, [load, activeCompanyId]);

  const toggle = async (rule) => {
    try { await api.post(`/automations/${rule.key}/toggle`, { enabled: !rule.enabled }); load(); }
    catch (e) { toast.error("Could not update rule"); }
  };
  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter an email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/email/test", { to: testTo.trim() });
      if (data.status === "sent") toast.success("Test email sent");
      else toast.error(`Email ${data.status}: ${data.error || ""}`);
      load();
    } catch (e) { toast.error("Send failed"); } finally { setSending(false); }
  };

  if (!data) return <Spinner />;

  return (
    <Scroller>
      <PageHeader title="Automations" subtitle={isGroupView ? "Group-wide default rules — fire automatically per business" : `Rules for ${activeCompany?.name || "this business"}`} icon={Zap} />

      {health && (
        <div className="mb-6 rounded-2xl border border-white/10 bg-charcoal p-5" data-testid="email-health">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5"><Mail className="h-5 w-5 text-gold" /><span className="font-heading font-semibold">Email connection</span></div>
            <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs ${health.provider_connected ? "border-emerald-400/30 text-emerald-300" : "border-red-400/30 text-red-300"}`} data-testid="email-status">
              {health.provider_connected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {health.provider_connected ? "Connected" : "Not connected"}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[["Sent", health.counts.sent, "text-emerald-300"], ["Failed", health.counts.failed, "text-red-300"], ["Queued", health.counts.queued, "text-gold"]].map(([l, v, c]) => (
              <div key={l} className="rounded-xl border border-white/8 bg-graphite py-3"><p className={`font-heading text-xl font-bold ${c}`}>{v}</p><p className="text-[10px] uppercase tracking-wide text-white/35">{l}</p></div>
            ))}
          </div>
          <p className="mt-3 text-xs text-white/40" data-testid="email-sender-line">
            Business emails send as: <span className="text-white/80 font-medium">{health.from_name}</span>
            {health.reply_to ? <span className="text-white/50"> · reply-to {health.reply_to}</span> : ""}
          </p>
          <p className="mt-1 text-[11px] text-white/35">
            {health.is_group_view
              ? "Group view — select a business to send as that company's identity."
              : "Account & platform emails (billing, invitations, password reset) send as BDVG AI."}
          </p>
          {health.recent_failures?.length > 0 && (
            <p className="mt-2 text-xs text-red-300">{health.recent_failures.length} recent failure(s) — latest: {health.recent_failures[0].error}</p>
          )}
          <div className="mt-4 flex gap-2">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test email to…" className="flex-1 rounded-xl border border-white/10 bg-graphite px-3.5 py-2 text-sm focus:border-gold/40 focus:outline-none" data-testid="email-test-input" />
            <button onClick={sendTest} disabled={sending} className={goldBtn} data-testid="email-test-send">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test</button>
          </div>
        </div>
      )}

      <p className="mb-3 text-xs uppercase tracking-wider text-white/35">Workflow rules · {data.scope === "company" ? data.scope_name : "Group defaults"}</p>
      <div className="space-y-2">
        {data.rules.map((r) => (
          <div key={r.key} className="flex items-center justify-between rounded-xl border border-white/8 bg-charcoal px-4 py-3" data-testid={`rule-${r.key}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white/90">{r.name}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-white/30">on {r.trigger}</span>
                {r.actions.map((a) => {
                  const Ai = ACTION_ICON[a] || Bell;
                  return <span key={a} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-white/50"><Ai className="h-3 w-3" />{a}</span>;
                })}
              </div>
            </div>
            <button onClick={() => can("settings.manage") && toggle(r)} disabled={!can("settings.manage")}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${r.enabled ? "bg-gold" : "bg-white/15"} ${!can("settings.manage") ? "opacity-50" : ""}`} data-testid={`rule-toggle-${r.key}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-black transition-all ${r.enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-white/30">Toggles apply to {data.scope === "company" ? "this business only" : "the whole group (defaults)"}. Switch business to set per-company overrides.</p>
    </Scroller>
  );
}
